/*
 * 3-step picker (Material, Frame, Size) for the product page, 2026-10-01.
 * Markup: snippets/product-step-picker.liquid. It sets the hidden native
 * <variant-selects> inputs and fires their change event, so product-info.js
 * keeps doing the price, URL, media, buy button and cart updates. It lives
 * outside <variant-selects>, so Dawn's re-render does not replace it; it syncs
 * back from Dawn on the variantChange pub/sub event.
 */
if (!customElements.get('step-picker')) {
  customElements.define(
    'step-picker',
    class StepPicker extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-step-picker-data]');
        if (!json) return;
        this.data = JSON.parse(json.textContent);
        this.sectionId = this.dataset.section;
        this.byKey = new Map(this.data.variants.map((v) => [`${v.f}\n${v.s}`, v]));
        this.sizes = Array.from(this.querySelectorAll('[data-step="size"] input'), (i) => i.value);

        const selected = this.data.variants.find((v) => v.id === this.data.selected) || this.data.variants[0];
        this.frame = this.querySelector('[data-step="frame"] input:checked')?.value || this.data.noFrameValue;
        this.setFromVariant(selected);

        this.addEventListener('change', this.onChange.bind(this));
        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.sectionId || !data.variant) return;
            this.showPrice();
            this.setFromVariant({
              f: data.variant.options[this.data.frameIndex],
              s: data.variant.options[this.data.sizeIndex],
            });
            if (!data.variant.featured_media) this.showFrameMedia();
          });
        }

        // Page opened on paper with no ?variant=: the snippet already shows the
        // no-frame canvas here; move Dawn's native picker (price, cart, media) to it.
        if (this.data.switchDefault) {
          Promise.all(['product-info', 'variant-selects'].map((n) => customElements.whenDefined(n))).then(() =>
            this.updateNative()
          );
          setTimeout(() => this.showPrice(), 3000);
        }
      }

      showPrice() {
        document.querySelector('[data-step-picker-hide]')?.remove();
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      isPaper(f) {
        return f === this.data.paperValue;
      }

      get frameValue() {
        return this.material === 'paper' ? this.data.paperValue : this.frame;
      }

      find(f, s) {
        return this.byKey.get(`${f}\n${s}`);
      }

      setFromVariant(v) {
        this.material = this.isPaper(v.f) ? 'paper' : 'canvas';
        if (!this.isPaper(v.f)) this.frame = v.f;
        this.size = v.s;
        this.render();
      }

      onChange(event) {
        const input = event.target;
        if (!input.matches('.step-picker__input')) return;
        const step = input.closest('[data-step]').dataset.step;
        if (step === 'material') this.material = input.value;
        if (step === 'frame') this.frame = input.value;
        if (step === 'size') this.size = input.value;

        // Keep the size if it exists for the new material/frame; else take the nearest one.
        if (!this.find(this.frameValue, this.size)) {
          const at = this.sizes.indexOf(this.size);
          const options = this.sizes
            .filter((s) => this.find(this.frameValue, s))
            .sort((a, b) => Math.abs(this.sizes.indexOf(a) - at) - Math.abs(this.sizes.indexOf(b) - at));
          if (options.length) this.size = options[0];
        }

        this.render();
        this.updateNative();
      }

      render() {
        const paper = this.material === 'paper';
        this.querySelectorAll('[data-step="material"] input').forEach((i) => (i.checked = i.value === this.material));
        this.querySelector('[data-paper-note]').hidden = !paper;

        const frameStep = this.querySelector('[data-step="frame"]');
        frameStep.hidden = paper || !frameStep.querySelector('input');
        frameStep.querySelectorAll('input').forEach((i) => {
          i.checked = i.value === this.frame;
          if (i.checked) this.querySelector('[data-selected-frame]').textContent = i.dataset.label;
        });

        this.querySelectorAll('[data-step="size"] input').forEach((i) => {
          const v = this.find(this.frameValue, i.value);
          i.disabled = !v;
          i.checked = i.value === this.size;
        });
        this.querySelector('[data-selected-size]').textContent = this.size;
      }

      updateNative() {
        const root = this.closest('product-info') || document;
        const selects = root.querySelector('variant-selects');
        if (!selects || !this.find(this.frameValue, this.size)) return;
        const inputs = Array.from(selects.querySelectorAll('input[type="radio"]'));
        const pick = (name, value) => inputs.find((i) => i.dataset.optionName === name && i.value === value);
        const frameInput = pick(this.data.frameOption, this.frameValue);
        const sizeInput = pick(this.data.sizeOption, this.size);
        if (!frameInput || !sizeInput || (frameInput.checked && sizeInput.checked)) return;
        const changed = frameInput.checked ? sizeInput : frameInput;
        frameInput.checked = true;
        sizeInput.checked = true;
        changed.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Fallback for variants without their own image: thumb_<color> or canvas_ media.
      showFrameMedia() {
        const v = this.find(this.frameValue, this.size);
        if (!v || this.material === 'paper') return;
        const color = this.frame.match(/\(([^)]+)\)/)?.[1]?.toLowerCase();
        const prefix = this.frame === this.data.noFrameValue ? 'canvas_' : `thumb_${color}`;
        const media = this.data.media.find((m) => m.f.toLowerCase().startsWith(prefix));
        const gallery = (this.closest('product-info') || document).querySelector('media-gallery');
        if (media) gallery?.setActiveMedia?.(`${this.sectionId}-${media.id}`, true);
      }
    }
  );
}
